import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { PrintDestinationRole } from '@/lib/shared-types';
import { useRequireScope } from '@/Hooks/useRequireScope';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  FLEET_ROUTES,
  type CreateDestinationBody,
  type FleetDestination,
  type FleetPrinter,
} from '../fleetApi';
import { apiMessage, type BranchOption } from '../fleetHelpers';

const ROLES = Object.values(PrintDestinationRole);

/**
 * A logical print destination — "Hot line", "Front till", "Bar".
 *
 * The indirection is the point: swapping a dead printer for the spare is one
 * edit here, not a rewrite of every rule that pointed at the dead one. So the
 * printer fields are the ones an operator comes back to, and the code and branch
 * are fixed after creation — a destination is referenced by every rule aimed at
 * it, and moving it between branches would silently re-point them.
 *
 * The **fallback is resolved at claim time, not at enqueue**. A printer that
 * dies between the two is exactly the case a fallback exists for, which is why
 * it is worth setting even on a destination whose primary is healthy today.
 *
 * Both pickers offer only printers standing in the destination's own branch:
 * the server refuses anything else, and a cross-branch printer is physically
 * undeliverable anyway.
 */
export default function DestinationFormDialog({
  open,
  destination,
  branches,
  printers,
  defaultBranchId,
  onClose,
  onSaved,
}: {
  open: boolean;
  destination: FleetDestination | null;
  branches: BranchOption[];
  printers: FleetPrinter[];
  defaultBranchId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const requireScope = useRequireScope();

  const [branchId, setBranchId] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [role, setRole] = useState<PrintDestinationRole>('KITCHEN');
  const [printerId, setPrinterId] = useState('');
  const [fallbackPrinterId, setFallbackPrinterId] = useState('');
  const [copies, setCopies] = useState('1');
  const [isActive, setIsActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setBranchId(destination?.branchId ?? defaultBranchId ?? branches[0]?.id ?? '');
    setName(destination?.name ?? '');
    setCode(destination?.code ?? '');
    setRole(destination?.role ?? 'KITCHEN');
    setPrinterId(destination?.printerId ?? '');
    setFallbackPrinterId(destination?.fallbackPrinterId ?? '');
    setCopies(destination ? String(destination.copies) : '1');
    setIsActive(destination?.isActive ?? true);
  }, [open, destination, defaultBranchId, branches]);

  const branchPrinters = useMemo(
    () => printers.filter((printer) => printer.branchId === branchId && printer.isActive),
    [printers, branchId],
  );

  const codeValid = !code || /^[A-Z0-9_-]+$/.test(code);
  const outOfScope = !destination && Boolean(branchId) && !requireScope('BRANCH', branchId);

  const submit = async () => {
    if (!name.trim()) {
      setError(t('fleet.destinations.nameRequired'));
      return;
    }
    if (!destination && (!branchId || !code.trim())) {
      setError(t('fleet.destinations.branchAndCodeRequired'));
      return;
    }
    if (!codeValid) {
      setError(t('fleet.printers.codeFormat'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const shared = {
        name: name.trim(),
        role,
        printerId: printerId || undefined,
        fallbackPrinterId: fallbackPrinterId || undefined,
        copies: Number(copies) || 1,
      };
      if (destination) {
        await api.patch(FLEET_ROUTES.destination(destination.id), { ...shared, isActive });
        toast.success(t('fleet.destinations.updated'));
      } else {
        const body: CreateDestinationBody = { ...shared, branchId, code: code.trim() };
        await api.post(FLEET_ROUTES.destinations, body);
        toast.success(t('fleet.destinations.created'));
      }
      onSaved();
      onClose();
    } catch (requestError) {
      setError(apiMessage(requestError, t('fleet.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={destination ? t('fleet.destinations.editTitle') : t('fleet.destinations.addTitle')}
      description={t('fleet.destinations.formHint')}
      className="max-w-2xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={busy || outOfScope} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {!destination && (
          <Field
            label={t('fleet.common.branch')}
            required
            error={outOfScope ? t('fleet.printers.branchOutOfScope') : undefined}
          >
            <Select value={branchId} onChange={(event) => setBranchId(event.target.value)}>
              <option value="">{t('fleet.printers.chooseBranch')}</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label={t('fleet.common.name')} required>
          <Input value={name} onChange={(event) => setName(event.target.value)} maxLength={120} />
        </Field>

        {!destination && (
          <Field
            label={t('fleet.printers.code')}
            required
            hint={t('fleet.destinations.codeHint')}
            error={codeValid ? undefined : t('fleet.printers.codeFormat')}
          >
            <Input
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              maxLength={32}
              placeholder="HOTLINE"
            />
          </Field>
        )}

        <Field label={t('fleet.destinations.role')} required hint={t('fleet.destinations.roleHint')}>
          <Select
            value={role}
            onChange={(event) => setRole(event.target.value as PrintDestinationRole)}
          >
            {ROLES.map((option) => (
              <option key={option} value={option}>
                {t(`fleet.role.${option}`)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('fleet.destinations.primary')} hint={t('fleet.destinations.primaryHint')}>
          <Select value={printerId} onChange={(event) => setPrinterId(event.target.value)}>
            <option value="">{t('fleet.destinations.noPrinterOption')}</option>
            {branchPrinters.map((printer) => (
              <option key={printer.id} value={printer.id}>
                {printer.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('fleet.destinations.fallback')} hint={t('fleet.destinations.fallbackHint')}>
          <Select
            value={fallbackPrinterId}
            onChange={(event) => setFallbackPrinterId(event.target.value)}
          >
            <option value="">{t('fleet.destinations.noFallbackOption')}</option>
            {branchPrinters
              .filter((printer) => printer.id !== printerId)
              .map((printer) => (
                <option key={printer.id} value={printer.id}>
                  {printer.name}
                </option>
              ))}
          </Select>
        </Field>

        <Field label={t('fleet.destinations.copies')} hint={t('fleet.destinations.copiesHint')}>
          <Select value={copies} onChange={(event) => setCopies(event.target.value)}>
            {[1, 2, 3, 4, 5].map((option) => (
              <option key={option} value={String(option)}>
                {option}
              </option>
            ))}
          </Select>
        </Field>

        {destination && (
          <Field label={t('fleet.common.status')}>
            <Select
              value={isActive ? 'true' : 'false'}
              onChange={(event) => setIsActive(event.target.value === 'true')}
            >
              <option value="true">{t('fleet.rules.active')}</option>
              <option value="false">{t('fleet.destinations.inactive')}</option>
            </Select>
          </Field>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-4 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </Dialog>
  );
}
